import { useState, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { SERVER_URL } from "../../../utils/serverConfig";

/**
 * SSE client for /api/v1/org-intelligence-v3/metrics-stream (2026-08-30).
 *
 * Frames land in order and each one is applied the moment it arrives, so the
 * page fills progressively instead of waiting for the model:
 *   metrics → status… → blockers → signals → ai (headline/beats/blocker/
 *   automation, one per line as the model finishes it) → report → done
 * A cache hit sends `cached` + `report` + `done`.
 */
export function useOrgIntelStream() {
  const [metrics, setMetrics] = useState(null);
  const [status, setStatus] = useState(null);      // { step, steps, message, detail, ms }
  const [steps, setSteps] = useState([]);           // history of statuses, one per step
  const [blockers, setBlockers] = useState(null);   // { blockers, hoursAtRisk, health, analysed }
  const [signals, setSignals] = useState(null);     // { automations, departments, changes }
  const [ai, setAi] = useState({ headline: null, beats: null, blockers: {}, automations: {} });
  const [typing, setTyping] = useState(null);       // { kind, id, field, text } — what the model is writing right now
  const [report, setReport] = useState(null);
  const [cached, setCached] = useState(null);       // { generatedAt, ageMinutes }
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    setMetrics(null); setStatus(null); setSteps([]); setBlockers(null); setSignals(null);
    setAi({ headline: null, beats: null, blockers: {}, automations: {} });
    setReport(null); setCached(null); setError(null); setTyping(null);
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
  }, []);

  const run = useCallback(async (body) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    reset();
    setStreaming(true); setLimitReached(false);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/org-intelligence-v3/metrics-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
        signal: abortRef.current.signal,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (errData?.error?.code === "AI_LIMIT_REACHED") { setLimitReached(true); setStreaming(false); return; }
        throw new Error(errData?.message || `Request failed: ${res.statusText}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let d;
          try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
          switch (d.type) {
            case "metrics": { const { type, ...m } = d; setMetrics(m); break; }
            case "status": {
              const s = { step: d.step, steps: d.steps, message: d.message, detail: d.detail || null, ms: d.ms || null };
              setStatus(s);
              setSteps((prev) => { const n = prev.filter((x) => x.step !== s.step); n.push(s); return n.sort((a, b) => a.step - b.step); });
              break;
            }
            case "blockers": setBlockers({ blockers: d.blockers || [], hoursAtRisk: d.hoursAtRisk || 0, health: d.health || null, analysed: d.analysed || null }); break;
            case "signals": setSignals({ automations: d.automations || [], departments: d.departments || [], changes: d.changes || null }); break;
            case "ai_typing": setTyping({ kind: d.kind, id: d.id || null, field: d.field, text: d.text || "" }); break;
            case "ai":
              setTyping((cur) => (cur && cur.kind === d.kind && (cur.id || null) === (d.blocker?.id || d.automation?.id || null) ? null : cur));
              if (d.kind === "headline") setAi((p) => ({ ...p, headline: d.text }));
              else if (d.kind === "beats") setAi((p) => ({ ...p, beats: d.items }));
              else if (d.kind === "blocker" && d.blocker) setAi((p) => ({ ...p, blockers: { ...p.blockers, [d.blocker.id]: d.blocker } }));
              else if (d.kind === "automation" && d.automation) setAi((p) => ({ ...p, automations: { ...p.automations, [d.automation.id]: d.automation } }));
              break;
            case "cached": setCached({ generatedAt: d.generatedAt, ageMinutes: d.ageMinutes }); break;
            case "report": {
              const r = d.report;
              setReport(r);
              // A cached report skips the progressive frames — hydrate them from it.
              if (r) {
                if (r.metrics) setMetrics((m) => m || r.metrics);
                setBlockers({ blockers: r.blockers || [], hoursAtRisk: r.hoursAtRisk || 0, health: r.health || null, analysed: r.analysed || null });
                setSignals({ automations: r.automations || [], departments: r.departments || [], changes: r.changes || null });
                setAi({ headline: r.headline || null, beats: r.beats || null,
                  blockers: Object.fromEntries((r.blockers || []).map((b) => [b.id, b])),
                  automations: Object.fromEntries((r.automations || []).map((a) => [a.id, a])) });
              }
              break;
            }
            case "no_activity": setError(d.message); setStreaming(false); break;
            case "error": setError(d.message); toast.error(d.message); break;
            case "done": setStreaming(false); setStatus(null); setTyping(null); window.dispatchEvent(new Event("ai-credit-used")); break;
            default: break;
          }
        }
      }
      setStreaming(false);
    } catch (err) {
      if (err.name !== "AbortError") { setError(err.message); toast.error(err.message); }
      setStreaming(false);
    }
  }, [reset]);

  return { metrics, status, steps, blockers, signals, ai, typing, report, cached, streaming, error, limitReached, setLimitReached, run, stop, reset };
}
